import {Children, cloneElement, EffectCallback, FC, isValidElement, useEffect, useRef} from 'react';

import {isLatLngLiteral} from '@googlemaps/typescript-guards';
import {createCustomEqual} from 'fast-equals';

import {config} from 'config/env';

import {MapProps} from './types';

export const Map: FC<MapProps> = ({
  onClick,
  setMap,
  map,
  onIdle,
  children,
  style,
  onCenterChanged,
  onDragEnd,
  onZoomChanged,
  zoom,
  ...options
}) => {
  const mapDomRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | undefined>(map);
  const currentMapZoom = useRef<number>(zoom || config.DEFAULT_ZOOM_LEVEL);

  const onZoom = (e: any) => {
    if (!mapInstanceRef.current) {
      return;
    }
    if (e.deltaY > 0) {
      if (currentMapZoom.current <= 0) {
        return;
      }
      currentMapZoom.current -= config.ZOOM_POWER;
    } else {
      if (currentMapZoom.current >= 23) {
        return;
      }
      currentMapZoom.current += config.ZOOM_POWER;
    }

    mapInstanceRef.current.setZoom(currentMapZoom.current);
    onZoomChanged?.(mapInstanceRef.current);
  };

  useEffect(() => {
    if (mapDomRef.current && !map && setMap) {
      const newMap = new window.google.maps.Map(mapDomRef.current, {
        clickableIcons: false,
        scrollwheel: false,
        zoom: zoom || config.DEFAULT_ZOOM_LEVEL,
        disableDoubleClickZoom: true
      });
      setMap(newMap);
      mapInstanceRef.current = newMap;
      currentMapZoom.current = zoom || config.DEFAULT_ZOOM_LEVEL;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapDomRef, map]);

  // because React does not do deep comparisons, a custom hook is used
  // see discussion in https://github.com/googlemaps/js-samples/issues/946
  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, options]);

  useEffect(() => {
    if (map) {
      ['click', 'idle', 'center_changed', 'dragend'].forEach((eventName) =>
        google.maps.event.clearListeners(map, eventName)
      );

      if (onClick) {
        map.addListener('click', onClick);
      }

      if (map && onIdle) onIdle(map);

      if (onCenterChanged) {
        map.addListener('center_changed', () => onCenterChanged(map));
      }

      if (onDragEnd) {
        map.addListener('dragend', () => onDragEnd(map));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, onClick, onCenterChanged]);

  return (
    <>
      <div ref={mapDomRef} style={style} onWheel={onZoom} />
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          // set the map prop on the child component
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return cloneElement(child, {map});
        }
      })}
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const deepCompareEqualsForMaps = createCustomEqual((deepEqual) => (a: any, b: any) => {
  if (isLatLngLiteral(a) || a instanceof google.maps.LatLng || isLatLngLiteral(b) || b instanceof google.maps.LatLng) {
    return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
  }

  // TODO extend to other types

  // use fast-equals for other objects
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return deepEqual(a, b);
});

function useDeepCompareMemoize(value: any) {
  const ref = useRef();

  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function useDeepCompareEffectForMaps(callback: EffectCallback, dependencies: any[]) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(callback, dependencies.map(useDeepCompareMemoize));
}
